/**
 * /settings → Workpackages (admin-only).
 *
 * Beheer-UI voor workpackages: lijst, aanmaken, hernoemen + beschrijving
 * wijzigen, status togglen (open/closed), verwijderen. Eerste fase van
 * M6.5 (Folders & Base) — pure UI bovenop de bestaande M6-WP-CRUD-API,
 * geen schema-changes.
 *
 * Zie [`docs/architecture/mvp/M6.5-folders-acceptance.md`](../../../../docs/architecture/mvp/M6.5-folders-acceptance.md)
 * voor de scope. Base-WP-detection (W2) komt in de volgende fase; deze
 * versie toont alle WPs gelijk.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  type AiWriteMode,
  ApiError,
  api,
  type ExplorerTreeDTO,
  type FolderAccessDTO,
  type FolderAccessMode,
  type FolderDTO,
  type TeamDTO,
  type WorkPackageDTO,
} from "../api/client";
import { confirmDialog } from "../editor/confirmDialog";
import { resolveBilingual } from "../registry";
import { useSession } from "../state/session";
import { AiWriteSelect } from "./AiWriteSetting";
import { BranchesSettings } from "./BranchesSettings";

interface Props {
  workspaceId: string;
}

/** CustomEvent waar Editor.tsx / Explorer / FoldertreePanel op luisteren
 *  om hun workpackages-fetch te herhalen na een succesvolle CRUD vanuit
 *  deze settings-modal. Zonder dit zou de gebruiker een hard-refresh
 *  moeten doen om nieuwe/verwijderde workpackages in de dropdown te
 *  zien. */
function notifyWorkpackagesChanged(): void {
  window.dispatchEvent(new CustomEvent("ae:workpackages-changed"));
}

type Subtab = "workpackages" | "branches" | "folder-permissions" | "team-permissions" | "copy";

export function WorkpackagesSettings({ workspaceId }: Props) {
  const { t } = useTranslation();
  const [subtab, setSubtab] = useState<Subtab>("workpackages");
  const [workpackages, setWorkpackages] = useState<WorkPackageDTO[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  // Form-state voor "nieuw workpackage"
  const [draftName, setDraftName] = useState("");
  const [draftDescription, setDraftDescription] = useState("");

  // Edit-state — welke WP wordt momenteel inline bewerkt + draft-values
  const [editId, setEditId] = useState<string | null>(null);
  const [editName, setEditName] = useState("");
  const [editDescription, setEditDescription] = useState("");

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const res = await api.listWorkpackages(workspaceId, /* includeClosed = */ true);
      setWorkpackages(res.workpackages);
    } catch (err) {
      setError(explain(err));
    }
  }, [workspaceId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const createWorkpackage = async () => {
    const name = draftName.trim();
    if (!name) return;
    setBusy(true);
    setError(null);
    setInfo(null);
    try {
      const body: { name: string; description?: string | null } = { name };
      const desc = draftDescription.trim();
      if (desc) body.description = desc;
      const created = await api.createWorkpackage(workspaceId, body);
      setInfo(t("settings.workpackages.createdToast", { name: created.name }));
      setDraftName("");
      setDraftDescription("");
      await refresh();
      notifyWorkpackagesChanged();
    } catch (err) {
      setError(explain(err));
    } finally {
      setBusy(false);
    }
  };

  const startEdit = (wp: WorkPackageDTO) => {
    setEditId(wp.id);
    setEditName(wp.name);
    setEditDescription(wp.description ?? "");
  };
  const cancelEdit = () => {
    setEditId(null);
    setEditName("");
    setEditDescription("");
  };
  const commitEdit = async () => {
    if (!editId) return;
    const name = editName.trim();
    if (!name) return;
    setBusy(true);
    setError(null);
    setInfo(null);
    try {
      const desc = editDescription.trim();
      await api.patchWorkpackage(workspaceId, editId, {
        name,
        description: desc || null,
      });
      setInfo(t("settings.workpackages.updatedToast"));
      cancelEdit();
      await refresh();
      notifyWorkpackagesChanged();
    } catch (err) {
      setError(explain(err));
    } finally {
      setBusy(false);
    }
  };

  const setAiWrite = async (wp: WorkPackageDTO, aiWrite: AiWriteMode) => {
    setBusy(true);
    setError(null);
    setInfo(null);
    try {
      await api.patchWorkpackage(workspaceId, wp.id, { ai_write: aiWrite });
      setInfo(t("aiWrite.saved"));
      await refresh();
      notifyWorkpackagesChanged();
    } catch (err) {
      setError(explain(err));
    } finally {
      setBusy(false);
    }
  };

  const toggleStatus = async (wp: WorkPackageDTO) => {
    const next = wp.status === "open" ? "closed" : "open";
    setBusy(true);
    setError(null);
    setInfo(null);
    try {
      await api.patchWorkpackage(workspaceId, wp.id, { status: next });
      setInfo(
        next === "open"
          ? t("settings.workpackages.statusToastOpen", { name: wp.name })
          : t("settings.workpackages.statusToastClosed", { name: wp.name }),
      );
      await refresh();
      notifyWorkpackagesChanged();
    } catch (err) {
      setError(explain(err));
    } finally {
      setBusy(false);
    }
  };

  const deleteWorkpackage = async (wp: WorkPackageDTO) => {
    // Verwijdering cascadeert naar branches + concepten + diagrammen +
    // viewStates + snapshots. Hard bevestigen vóór de call zodat de
    // modelleur niet per ongeluk een gevuld WP wist.
    if (
      !(await confirmDialog({
        message: t("settings.workpackages.deleteConfirm", { name: wp.name }),
        danger: true,
      }))
    )
      return;
    setBusy(true);
    setError(null);
    setInfo(null);
    try {
      // Is dit de WP waarin de gebruiker NU werkt? Zo ja, kan 'ie niet
      // doorwerken in een verwijderde WP → hard reload (veilige reset van
      // canvas-tabs + store). Zo nee (het normale geval: een ongebruikte
      // WP opruimen vanuit Settings) → ZACHTE refresh, zodat het
      // Settings-venster open blijft en de context behouden wordt.
      const wasActiveWp = useSession.getState().workpackageId === wp.id;
      await api.deleteWorkpackage(workspaceId, wp.id);
      // Ruim de gepersisteerde WP-id in localStorage op wanneer 'ie naar
      // de zojuist verwijderde WP wijst (voorkomt "Workpackage not found"-
      // flash bij een latere bootstrap).
      try {
        const storageKey = `ae.session.workpackageId:${workspaceId}`;
        if (window.localStorage.getItem(storageKey) === wp.id) {
          window.localStorage.removeItem(storageKey);
        }
      } catch {
        // localStorage onbruikbaar — bootstrap-fallback vangt 't op.
      }
      if (wasActiveWp) {
        // Actieve WP verwijderd → hard reload (zie commit c736f3b).
        window.location.reload();
        return;
      }
      // Zachte refresh: lijst hier verversen + Explorer/Editor laten
      // herladen via het workpackages-changed-event. Geen page-reload,
      // venster blijft open.
      await refresh();
      notifyWorkpackagesChanged();
      setInfo(t("settings.workpackages.deletedToast", { name: wp.name }));
    } catch (err) {
      setError(explain(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col gap-5">
      <nav className="flex flex-wrap gap-1 border-(--ld-border) border-b">
        <SubtabButton active={subtab === "workpackages"} onClick={() => setSubtab("workpackages")}>
          {t("settings.workpackages.subtabWorkpackages")}
        </SubtabButton>
        <SubtabButton active={subtab === "branches"} onClick={() => setSubtab("branches")}>
          {t("settings.workpackages.subtabBranches")}
        </SubtabButton>
        <SubtabButton
          active={subtab === "folder-permissions"}
          onClick={() => setSubtab("folder-permissions")}
        >
          {t("settings.workpackages.subtabFolderPermissions")}
        </SubtabButton>
        <SubtabButton
          active={subtab === "team-permissions"}
          onClick={() => setSubtab("team-permissions")}
        >
          {t("settings.workpackages.subtabTeamPermissions")}
        </SubtabButton>
        <SubtabButton active={subtab === "copy"} onClick={() => setSubtab("copy")}>
          {t("settings.workpackages.subtabCopy")}
        </SubtabButton>
      </nav>

      {subtab === "workpackages" && (
        <>
          <section className="rounded-lg border border-(--ld-border) bg-(--ld-surface) p-5">
            <h2 className="font-semibold text-base text-(--ld-text)">
              {t("settings.workpackages.newTitle")}
            </h2>
            <p className="mt-1 text-(--ld-muted) text-sm">
              {t("settings.workpackages.newDescription")}
            </p>
            <div className="mt-4 flex flex-col gap-2">
              <label className="flex flex-col gap-1 text-sm">
                <span className="text-(--ld-text)">{t("settings.workpackages.nameLabel")}</span>
                <input
                  className="rounded border border-(--ld-control) bg-(--ld-surface) px-3 py-2 text-sm focus:border-(--ld-accent) focus:outline-none"
                  type="text"
                  value={draftName}
                  onChange={(e) => setDraftName(e.target.value)}
                  placeholder={t("settings.workpackages.namePlaceholder")}
                  disabled={busy}
                  maxLength={120}
                />
              </label>
              <label className="flex flex-col gap-1 text-sm">
                <span className="text-(--ld-text)">
                  {t("settings.workpackages.descriptionLabel")}
                </span>
                <textarea
                  className="rounded border border-(--ld-control) bg-(--ld-surface) px-3 py-2 text-sm focus:border-(--ld-accent) focus:outline-none"
                  value={draftDescription}
                  onChange={(e) => setDraftDescription(e.target.value)}
                  placeholder={t("settings.workpackages.descriptionPlaceholder")}
                  disabled={busy}
                  rows={2}
                />
              </label>
              <div>
                <button
                  type="button"
                  onClick={() => void createWorkpackage()}
                  disabled={busy || !draftName.trim()}
                  className="rounded bg-(--ld-primary) px-3 py-1.5 font-medium text-sm text-(--ld-primary-ink) hover:bg-(--ld-primary-hover) disabled:bg-(--ld-disabled-bg) disabled:text-(--ld-disabled-ink)"
                >
                  {t("settings.workpackages.createButton")}
                </button>
              </div>
            </div>
          </section>

          <section className="rounded-lg border border-(--ld-border) bg-(--ld-surface) p-5">
            <h2 className="font-semibold text-base text-(--ld-text)">
              {t("settings.workpackages.listTitle")}
            </h2>
            <p className="mt-1 text-(--ld-muted) text-sm">
              {t("settings.workpackages.listDescription")}
            </p>
            {error && (
              <p className="mt-3 rounded border border-(--ld-control) bg-(--ld-negative-bg) px-3 py-2 text-(--ld-negative-ink) text-sm">
                {error}
              </p>
            )}
            {info && (
              <p className="mt-3 rounded border border-transparent bg-(--ld-positive-bg) px-3 py-2 text-(--ld-positive-ink) text-sm">
                {info}
              </p>
            )}
            {workpackages.length === 0 ? (
              <p className="mt-4 text-(--ld-muted) text-sm italic">
                {t("settings.workpackages.empty")}
              </p>
            ) : (
              <ul className="mt-4 flex flex-col gap-2">
                {workpackages.map((wp) => {
                  const isEditing = editId === wp.id;
                  const isClosed = wp.status === "closed";
                  const isBase = wp.isBase;
                  return (
                    <li
                      key={wp.id}
                      className={`rounded border px-3 py-2 ${
                        isBase
                          ? "border-(--ld-border) bg-(--ld-raised)"
                          : isClosed
                            ? "border-(--ld-border) bg-(--ld-raised)"
                            : "border-(--ld-control) bg-(--ld-surface)"
                      }`}
                    >
                      {isEditing ? (
                        <div className="flex flex-col gap-2">
                          <input
                            className="rounded border border-(--ld-accent) bg-(--ld-surface) px-2 py-1 text-sm focus:outline-none"
                            type="text"
                            value={editName}
                            onChange={(e) => setEditName(e.target.value)}
                            placeholder={t("settings.workpackages.editPlaceholderName")}
                            disabled={busy}
                            maxLength={120}
                          />
                          <textarea
                            className="rounded border border-(--ld-accent) bg-(--ld-surface) px-2 py-1 text-sm focus:outline-none"
                            value={editDescription}
                            onChange={(e) => setEditDescription(e.target.value)}
                            placeholder={t("settings.workpackages.editPlaceholderDescription")}
                            disabled={busy}
                            rows={2}
                          />
                          <div className="flex gap-2">
                            <button
                              type="button"
                              onClick={() => void commitEdit()}
                              disabled={busy || !editName.trim()}
                              className="rounded bg-(--ld-primary) px-3 py-1 font-medium text-sm text-(--ld-primary-ink) hover:bg-(--ld-primary-hover) disabled:bg-(--ld-disabled-bg) disabled:text-(--ld-disabled-ink)"
                            >
                              {t("settings.workpackages.saveButton")}
                            </button>
                            <button
                              type="button"
                              onClick={cancelEdit}
                              disabled={busy}
                              className="rounded border border-(--ld-control) bg-(--ld-surface) px-3 py-1 font-medium text-(--ld-text) text-sm hover:bg-(--ld-raised)"
                            >
                              {t("settings.common.cancel")}
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2">
                              <span
                                className={`truncate font-medium ${
                                  isClosed ? "text-(--ld-muted) line-through" : "text-(--ld-text)"
                                }`}
                              >
                                {wp.name}
                              </span>
                              {isBase && (
                                <span
                                  className="rounded bg-(--ld-active) px-1.5 py-0.5 text-xs text-(--ld-text)"
                                  title={t("settings.workpackages.baseBadgeTitle")}
                                >
                                  {t("settings.workpackages.baseBadge")}
                                </span>
                              )}
                              {isClosed && (
                                <span className="rounded bg-(--ld-active) px-1.5 py-0.5 text-xs text-(--ld-text)">
                                  {t("settings.workpackages.closedBadge")}
                                </span>
                              )}
                            </div>
                            {wp.description && (
                              <p className="mt-0.5 text-(--ld-muted) text-xs">{wp.description}</p>
                            )}
                            <div className="mt-1">
                              <AiWriteSelect
                                value={wp.aiWrite ?? "ai_plateau"}
                                disabled={busy}
                                onChange={(next) => void setAiWrite(wp, next)}
                              />
                            </div>
                            <p className="mt-1 text-(--ld-muted) text-xs">
                              {t("settings.workpackages.createdByOn", {
                                user: wp.createdBy,
                                date: wp.createdAt,
                              })}
                            </p>
                          </div>
                          <div className="flex shrink-0 gap-1">
                            <button
                              type="button"
                              onClick={() => startEdit(wp)}
                              disabled={busy || isBase}
                              className="rounded border border-(--ld-control) bg-(--ld-surface) px-2 py-1 text-(--ld-text) text-xs hover:bg-(--ld-raised) disabled:opacity-40"
                              title={
                                isBase
                                  ? t("settings.workpackages.editTitleBase")
                                  : t("settings.workpackages.editTitleEnabled")
                              }
                            >
                              {t("settings.workpackages.editButton")}
                            </button>
                            <button
                              type="button"
                              onClick={() => void toggleStatus(wp)}
                              disabled={busy || isBase}
                              className="rounded border border-(--ld-control) bg-(--ld-surface) px-2 py-1 text-(--ld-text) text-xs hover:bg-(--ld-raised) disabled:opacity-40"
                              title={
                                isBase
                                  ? t("settings.workpackages.toggleTitleBase")
                                  : isClosed
                                    ? t("settings.workpackages.toggleTitleReopen")
                                    : t("settings.workpackages.toggleTitleClose")
                              }
                            >
                              {isClosed
                                ? t("settings.workpackages.reopenButton")
                                : t("settings.workpackages.closeButton")}
                            </button>
                            <button
                              type="button"
                              onClick={() => void deleteWorkpackage(wp)}
                              disabled={busy || isBase}
                              className="rounded border border-(--ld-control) bg-(--ld-surface) px-2 py-1 text-(--ld-negative) text-xs hover:bg-(--ld-negative-bg) disabled:opacity-40"
                              title={
                                isBase
                                  ? t("settings.workpackages.deleteTitleBase")
                                  : t("settings.workpackages.deleteTitleEnabled")
                              }
                            >
                              {t("settings.workpackages.deleteButton")}
                            </button>
                          </div>
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </section>
        </>
      )}

      {subtab === "branches" && (
        <BranchesSettings workspaceId={workspaceId} workpackages={workpackages} />
      )}

      {subtab === "folder-permissions" && (
        <>
          {/* M6.5-W3b — FoldersSection verhuisd naar FoldertreePanel in Base-WP.
              Hier blijft alleen de R/W-subscriptions-UI per WP. */}
          <FolderAccessSection workspaceId={workspaceId} workpackages={workpackages} />
        </>
      )}

      {subtab === "team-permissions" && (
        <TeamPermissionsSection workspaceId={workspaceId} workpackages={workpackages} />
      )}

      {subtab === "copy" && (
        <CrossWorkpackageCopySection workspaceId={workspaceId} workpackages={workpackages} />
      )}
    </div>
  );
}

function SubtabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`-mb-px whitespace-nowrap rounded-t border-b-2 px-3 py-1.5 font-medium text-sm ${
        active
          ? "border-(--ld-accent) text-(--ld-accent)"
          : "border-transparent text-(--ld-muted) hover:text-(--ld-text)"
      }`}
    >
      {children}
    </button>
  );
}

/**
 * M6.5-W4 — Folder-subscriptions beheren per (non-Base) workpackage.
 *
 * Admin kiest een WP uit een dropdown; daaronder verschijnt de folder-
 * lijst uit Base, met per folder een mode-selector (— / read / write).
 * `write`-optie disabled wanneer een andere WP al write-rechten heeft —
 * unique-partial-index in DB voorkomt anders een 409.
 */
function FolderAccessSection({
  workspaceId,
  workpackages,
}: {
  workspaceId: string;
  workpackages: WorkPackageDTO[];
}) {
  const { t } = useTranslation();
  // Base + closed-WPs eruit voor de subscription-selector — alleen
  // open non-Base-WPs kunnen iets subscriben.
  const selectableWps = useMemo(
    () => workpackages.filter((wp) => !wp.isBase && wp.status === "open"),
    [workpackages],
  );
  const [selectedWpId, setSelectedWpId] = useState<string | null>(null);
  const [folders, setFolders] = useState<FolderDTO[]>([]);
  const [access, setAccess] = useState<FolderAccessDTO[]>([]);
  const [writeHolders, setWriteHolders] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  // Default-WP-keuze zodra workpackages binnen zijn.
  useEffect(() => {
    if (!selectedWpId && selectableWps[0]) {
      setSelectedWpId(selectableWps[0].id);
    }
  }, [selectableWps, selectedWpId]);

  const refresh = useCallback(async () => {
    if (!selectedWpId) {
      setAccess([]);
      setWriteHolders({});
      return;
    }
    setError(null);
    try {
      const [foldersRes, accessRes] = await Promise.all([
        api.listFolders(workspaceId),
        api.listFolderAccess(workspaceId, selectedWpId),
      ]);
      setFolders(foldersRes.folders);
      setAccess(accessRes.access);
      setWriteHolders(accessRes.writeHolders);
    } catch (err) {
      setError(explain(err));
    }
  }, [workspaceId, selectedWpId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const accessByFolder = useMemo(() => {
    const map = new Map<string, FolderAccessMode>();
    for (const a of access) map.set(a.folderId, a.mode);
    return map;
  }, [access]);

  const wpNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const wp of workpackages) map.set(wp.id, wp.name);
    return map;
  }, [workpackages]);

  const setMode = async (folderId: string, mode: FolderAccessMode | "none") => {
    if (!selectedWpId) return;
    setBusy(true);
    setError(null);
    setInfo(null);
    try {
      if (mode === "none") {
        await api.revokeFolderAccess(workspaceId, selectedWpId, folderId);
        setInfo(t("settings.workpackages.folderRevoked"));
      } else {
        await api.setFolderAccess(workspaceId, selectedWpId, folderId, mode);
        setInfo(t("settings.workpackages.folderSet", { mode }));
      }
      await refresh();
      notifyWorkpackagesChanged();
    } catch (err) {
      setError(explain(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="rounded-lg border border-(--ld-border) bg-(--ld-surface) p-5">
      <h2 className="font-semibold text-base text-(--ld-text)">
        {t("settings.workpackages.folderSectionTitle")}
      </h2>
      <p className="mt-1 text-(--ld-muted) text-sm">
        {t("settings.workpackages.folderSectionDescriptionBefore")}
        <strong>{t("settings.workpackages.folderSectionRead")}</strong>
        {t("settings.workpackages.folderSectionAnd")}
        <strong>{t("settings.workpackages.folderSectionWrite")}</strong>
        {t("settings.workpackages.folderSectionDescriptionMid")}
        <strong>{t("settings.workpackages.folderSectionWrite")}</strong>
        {t("settings.workpackages.folderSectionDescriptionEnd")}
      </p>

      {selectableWps.length === 0 ? (
        <p className="mt-4 text-(--ld-muted) text-sm italic">
          {t("settings.workpackages.folderEmptyNoWps")}
        </p>
      ) : folders.length === 0 ? (
        <p className="mt-4 text-(--ld-muted) text-sm italic">
          {t("settings.workpackages.folderEmptyNoFolders")}
        </p>
      ) : (
        <>
          <label className="mt-4 flex flex-col gap-1 text-sm">
            <span className="text-(--ld-text)">{t("settings.workpackages.workpackageSelect")}</span>
            <select
              className="rounded border border-(--ld-control) bg-(--ld-surface) px-3 py-2 text-sm focus:border-(--ld-accent) focus:outline-none"
              value={selectedWpId ?? ""}
              onChange={(e) => setSelectedWpId(e.target.value || null)}
              disabled={busy}
            >
              {selectableWps.map((wp) => (
                <option key={wp.id} value={wp.id}>
                  {wp.name}
                </option>
              ))}
            </select>
          </label>

          {error && (
            <p className="mt-3 rounded border border-(--ld-control) bg-(--ld-negative-bg) px-3 py-2 text-(--ld-negative-ink) text-sm">
              {error}
            </p>
          )}
          {info && (
            <p className="mt-3 rounded border border-transparent bg-(--ld-positive-bg) px-3 py-2 text-(--ld-positive-ink) text-sm">
              {info}
            </p>
          )}

          <table className="mt-4 w-full text-sm">
            <thead>
              <tr className="border-(--ld-border) border-b text-left text-(--ld-muted) text-xs">
                <th className="py-2 font-medium">{t("settings.workpackages.folderColFolder")}</th>
                <th className="py-2 font-medium">
                  {t("settings.workpackages.folderColSubscription")}
                </th>
                <th className="py-2 font-medium">
                  {t("settings.workpackages.folderColWriteHolder")}
                </th>
              </tr>
            </thead>
            <tbody>
              {folders.map((f) => {
                const current = accessByFolder.get(f.id) ?? "none";
                const writeHolderId = writeHolders[f.id];
                const writeHolderIsOther = writeHolderId && writeHolderId !== selectedWpId;
                const writeDisabled = busy || Boolean(writeHolderIsOther);
                return (
                  <tr key={f.id} className="border-(--ld-rule) border-b">
                    <td className="py-2 pr-3">
                      <div className="font-medium text-(--ld-text)">{f.name}</div>
                      {f.description && (
                        <div className="text-(--ld-muted) text-xs">{f.description}</div>
                      )}
                    </td>
                    <td className="py-2 pr-3">
                      <div className="flex gap-1">
                        {(["none", "read", "write"] as const).map((opt) => {
                          const isActive = current === opt;
                          const optDisabled = busy || (opt === "write" && writeDisabled);
                          return (
                            <button
                              key={opt}
                              type="button"
                              onClick={() => void setMode(f.id, opt)}
                              disabled={optDisabled || isActive}
                              className={`rounded border px-2 py-0.5 text-xs ${
                                isActive
                                  ? "border-(--ld-accent) bg-(--ld-active) font-medium text-(--ld-accent)"
                                  : "border-(--ld-control) bg-(--ld-surface) text-(--ld-text) hover:bg-(--ld-raised)"
                              } ${optDisabled && !isActive ? "opacity-40" : ""}`}
                              title={
                                opt === "write" && writeHolderIsOther
                                  ? t("settings.workpackages.folderWriteHeldBy", {
                                      name: wpNameById.get(writeHolderId) ?? writeHolderId,
                                    })
                                  : undefined
                              }
                            >
                              {opt === "none" ? "—" : opt}
                            </button>
                          );
                        })}
                      </div>
                    </td>
                    <td className="py-2 pr-3 text-(--ld-muted) text-xs">
                      {writeHolderId
                        ? writeHolderId === selectedWpId
                          ? t("settings.workpackages.folderThisWp")
                          : (wpNameById.get(writeHolderId) ?? writeHolderId)
                        : "—"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </>
      )}
    </section>
  );
}

/**
 * M6 — Team-permissies per workpackage.
 *
 * Admin kiest een WP en stelt per team in: `—` (geen toegang), `read`
 * (zichtbaar maar niet muteerbaar) of `write` (full edit). Het
 * Admin-team is uitgesloten — die heeft via z'n `role='admin'`-sync al
 * onbeperkte toegang tot alle WPs in de workspace.
 */
function TeamPermissionsSection({
  workspaceId,
  workpackages,
}: {
  workspaceId: string;
  workpackages: WorkPackageDTO[];
}) {
  const { t } = useTranslation();
  const selectableWps = useMemo(
    () => workpackages.filter((wp) => !wp.isBase && wp.status === "open"),
    [workpackages],
  );
  const [selectedWpId, setSelectedWpId] = useState<string | null>(null);
  const [teams, setTeams] = useState<TeamDTO[]>([]);
  const [perms, setPerms] = useState<
    Array<{ teamId: string; workpackageId: string; permission: "read" | "write" }>
  >([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  useEffect(() => {
    if (!selectedWpId && selectableWps[0]) setSelectedWpId(selectableWps[0].id);
  }, [selectableWps, selectedWpId]);

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const [teamsRes, permsRes] = await Promise.all([
        api.listTeams(workspaceId),
        api.listWorkpackagePermissions(workspaceId),
      ]);
      setTeams(teamsRes.teams);
      setPerms(permsRes.permissions);
    } catch (err) {
      setError(explain(err));
    }
  }, [workspaceId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const permByTeam = useMemo(() => {
    const map = new Map<string, "read" | "write">();
    if (!selectedWpId) return map;
    for (const p of perms) {
      if (p.workpackageId === selectedWpId) map.set(p.teamId, p.permission);
    }
    return map;
  }, [perms, selectedWpId]);

  const grantableTeams = useMemo(() => teams.filter((t) => !t.isAdmin), [teams]);

  const setMode = async (teamId: string, mode: "read" | "write" | "none") => {
    if (!selectedWpId) return;
    setBusy(true);
    setError(null);
    setInfo(null);
    try {
      if (mode === "none") {
        await api.revokeWorkpackagePermission(workspaceId, teamId, selectedWpId);
        setInfo(t("settings.workpackages.teamRevoked"));
      } else {
        await api.setWorkpackagePermission(workspaceId, teamId, selectedWpId, mode);
        setInfo(t("settings.workpackages.teamSet", { mode }));
      }
      await refresh();
      notifyWorkpackagesChanged();
    } catch (err) {
      setError(explain(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="rounded-lg border border-(--ld-border) bg-(--ld-surface) p-5">
      <h2 className="font-semibold text-base text-(--ld-text)">
        {t("settings.workpackages.teamSectionTitle")}
      </h2>
      <p className="mt-1 text-(--ld-muted) text-sm">
        {t("settings.workpackages.teamSectionDescriptionBefore")}
        <strong>{t("settings.workpackages.folderSectionRead")}</strong>
        {t("settings.workpackages.teamSectionDescriptionMid")}
        <strong>{t("settings.workpackages.folderSectionWrite")}</strong>
        {t("settings.workpackages.teamSectionDescriptionEnd")}
      </p>

      {selectableWps.length === 0 ? (
        <p className="mt-4 text-(--ld-muted) text-sm italic">
          {t("settings.workpackages.teamEmptyNoWps")}
        </p>
      ) : grantableTeams.length === 0 ? (
        <p className="mt-4 text-(--ld-muted) text-sm italic">
          {t("settings.workpackages.teamEmptyNoTeams")}
        </p>
      ) : (
        <>
          <label className="mt-4 flex flex-col gap-1 text-sm">
            <span className="text-(--ld-text)">{t("settings.workpackages.workpackageSelect")}</span>
            <select
              className="rounded border border-(--ld-control) bg-(--ld-surface) px-3 py-2 text-sm focus:border-(--ld-accent) focus:outline-none"
              value={selectedWpId ?? ""}
              onChange={(e) => setSelectedWpId(e.target.value || null)}
              disabled={busy}
            >
              {selectableWps.map((wp) => (
                <option key={wp.id} value={wp.id}>
                  {wp.name}
                </option>
              ))}
            </select>
          </label>

          {error && (
            <p className="mt-3 rounded border border-(--ld-control) bg-(--ld-negative-bg) px-3 py-2 text-(--ld-negative-ink) text-sm">
              {error}
            </p>
          )}
          {info && (
            <p className="mt-3 rounded border border-transparent bg-(--ld-positive-bg) px-3 py-2 text-(--ld-positive-ink) text-sm">
              {info}
            </p>
          )}

          <table className="mt-4 w-full text-sm">
            <thead>
              <tr className="border-(--ld-border) border-b text-left text-(--ld-muted) text-xs">
                <th className="py-2 font-medium">{t("settings.workpackages.teamColTeam")}</th>
                <th className="py-2 font-medium">{t("settings.workpackages.teamColAccess")}</th>
              </tr>
            </thead>
            <tbody>
              {grantableTeams.map((team) => {
                const current = permByTeam.get(team.id) ?? "none";
                return (
                  <tr key={team.id} className="border-(--ld-rule) border-b">
                    <td className="py-2 pr-3">
                      <div className="font-medium text-(--ld-text)">{team.name}</div>
                      {team.description && (
                        <div className="text-(--ld-muted) text-xs">{team.description}</div>
                      )}
                    </td>
                    <td className="py-2 pr-3">
                      <div className="flex gap-1">
                        {(["none", "read", "write"] as const).map((opt) => {
                          const isActive = current === opt;
                          return (
                            <button
                              key={opt}
                              type="button"
                              onClick={() => void setMode(team.id, opt)}
                              disabled={busy || isActive}
                              className={`rounded border px-2 py-0.5 text-xs ${
                                isActive
                                  ? "border-(--ld-accent) bg-(--ld-active) font-medium text-(--ld-accent)"
                                  : "border-(--ld-control) bg-(--ld-surface) text-(--ld-text) hover:bg-(--ld-raised)"
                              }`}
                            >
                              {opt === "none" ? "—" : opt}
                            </button>
                          );
                        })}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </>
      )}
    </section>
  );
}

/**
 * M6.5-W7 — Concepten kopiëren tussen workpackages.
 *
 * Source-WP-selector → laadt concepten uit z'n main-branch → checkboxes.
 * Target-WP-selector + mode-radio (match / always-new) + dry-run-preview
 * + Kopiëren-knop. Result-table met counts + per-id detail (reused /
 * created / skipped + reden).
 */
/**
 * Mini Explorer-tree-selector — toont per laag de groepen (object-types
 * of view-kinds) en items, met checkboxes voor selectie. Gebruikt door
 * de cross-WP-copy-flow met twee instanties naast elkaar: links de
 * objecten, rechts de diagrammen.
 */
function ExplorerSelector({
  title,
  tree,
  kind,
  collapsedLayers,
  toggleLayer,
  selectedIds,
  toggleItem,
  toggleGroup,
  busy,
}: {
  title: string;
  tree: ExplorerTreeDTO | null;
  kind: "concept" | "diagram";
  collapsedLayers: Set<string>;
  toggleLayer: (layer: string) => void;
  selectedIds: Set<string>;
  toggleItem: (id: string) => void;
  toggleGroup: (ids: string[]) => void;
  busy: boolean;
}) {
  const { t, i18n } = useTranslation();
  if (!tree) {
    return (
      <div className="rounded border border-(--ld-border) px-3 py-2">
        <h4 className="font-medium text-(--ld-text) text-xs">{title}</h4>
        <p className="mt-1 text-(--ld-muted) text-xs italic">
          {t("settings.workpackages.explorerLoading")}
        </p>
      </div>
    );
  }

  const layersWithItems = tree.layers.filter((l) =>
    kind === "concept" ? l.objectGroups.length > 0 : l.diagramGroups.length > 0,
  );

  return (
    <div className="rounded border border-(--ld-border)">
      <div className="border-(--ld-border) border-b bg-(--ld-raised) px-3 py-1.5">
        <h4 className="font-medium text-(--ld-text) text-xs">{title}</h4>
      </div>
      {layersWithItems.length === 0 ? (
        <p className="px-3 py-2 text-(--ld-muted) text-xs italic">
          {t("settings.workpackages.explorerEmpty", { title: title.toLowerCase() })}
        </p>
      ) : (
        <div className="max-h-96 overflow-y-auto">
          {layersWithItems.map((layer) => {
            const layerKey = `${kind}:${layer.layer}`;
            const layerCollapsed = collapsedLayers.has(layerKey);
            const layerLabel =
              layer.layer === "drawing"
                ? t("settings.workpackages.layerDrawing")
                : (LAYER_LABEL[layer.layer] ?? layer.layer);
            const groups = kind === "concept" ? layer.objectGroups : layer.diagramGroups;
            return (
              <div key={layer.layer} className="border-(--ld-rule) border-b last:border-b-0">
                <button
                  type="button"
                  onClick={() => toggleLayer(layerKey)}
                  className="flex w-full items-center gap-2 bg-(--ld-raised) px-2 py-1 text-left font-semibold text-(--ld-text) text-xs hover:bg-(--ld-raised)"
                >
                  <span>{layerCollapsed ? "▸" : "▾"}</span>
                  <span>{layerLabel}</span>
                  <span className="ml-auto text-(--ld-muted) text-xs">
                    {groups.reduce((n, g) => n + g.count, 0)}
                  </span>
                </button>
                {!layerCollapsed && (
                  <div>
                    {groups.map((g) => {
                      const groupIds = g.items.map((i) => i.id);
                      const allSelected =
                        groupIds.length > 0 && groupIds.every((id) => selectedIds.has(id));
                      return (
                        <div key={g.typeId}>
                          <button
                            type="button"
                            onClick={() => toggleGroup(groupIds)}
                            disabled={busy}
                            className="flex w-full items-center gap-2 px-3 py-1 text-left text-(--ld-muted) text-xs hover:bg-(--ld-raised)"
                          >
                            <input
                              type="checkbox"
                              checked={allSelected}
                              readOnly
                              className="pointer-events-none"
                            />
                            <span className="truncate font-medium">{g.typeId}</span>
                            <span className="ml-auto text-(--ld-muted)">{g.count}</span>
                          </button>
                          <ul>
                            {g.items.map((item) => (
                              <li
                                key={item.id}
                                className="flex items-center gap-2 px-6 py-0.5 text-sm hover:bg-(--ld-raised)"
                              >
                                <input
                                  type="checkbox"
                                  checked={selectedIds.has(item.id)}
                                  onChange={() => toggleItem(item.id)}
                                  disabled={busy}
                                />
                                <span className="truncate text-(--ld-text)">
                                  {resolveBilingual(item.name, i18n.language)}
                                </span>
                                {kind === "concept" &&
                                  "tombstoneInBranch" in item &&
                                  item.tombstoneInBranch && (
                                    <span className="ml-auto text-(--ld-negative) text-xs italic">
                                      {t("settings.workpackages.explorerTombstoned")}
                                    </span>
                                  )}
                              </li>
                            ))}
                          </ul>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const LAYER_LABEL: Record<string, string> = {
  archimate: "ArchiMate",
  c4: "C4",
  sharp: "Data modeling",
  bmc: "BMC",
};

type CopyPassResult = { reused: number; created: number; skipped: number };
type CopyDetail = {
  kind: "concept" | "relation" | "diagram";
  sourceId: string;
  action: "reused" | "created" | "skipped";
  name: { nl?: string; en?: string };
  typeId: string;
  reason?: string;
};

function CrossWorkpackageCopySection({
  workspaceId,
  workpackages,
}: {
  workspaceId: string;
  workpackages: WorkPackageDTO[];
}) {
  const { t, i18n } = useTranslation();
  const selectableWps = useMemo(
    () => workpackages.filter((wp) => !wp.isBase && wp.status === "open"),
    [workpackages],
  );
  const [sourceWpId, setSourceWpId] = useState<string | null>(null);
  const [targetWpId, setTargetWpId] = useState<string | null>(null);
  const [tree, setTree] = useState<ExplorerTreeDTO | null>(null);
  const [collapsedLayers, setCollapsedLayers] = useState<Set<string>>(new Set());
  const [selectedConceptIds, setSelectedConceptIds] = useState<Set<string>>(new Set());
  const [selectedDiagramIds, setSelectedDiagramIds] = useState<Set<string>>(new Set());
  const [mode, setMode] = useState<"match" | "always-new">("match");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [result, setResult] = useState<{
    concepts: CopyPassResult;
    relations: CopyPassResult;
    diagrams: CopyPassResult;
    details: CopyDetail[];
    wasDryRun: boolean;
  } | null>(null);

  // Init: source = eerste, target = tweede (of leeg).
  useEffect(() => {
    if (!sourceWpId && selectableWps[0]) setSourceWpId(selectableWps[0].id);
    if (!targetWpId && selectableWps[1]) setTargetWpId(selectableWps[1].id);
  }, [selectableWps, sourceWpId, targetWpId]);

  // Explorer-tree uit source-WP laden — hergebruikt het Explorer-DTO
  // zodat de selectie-UI dezelfde hiërarchie heeft als wat de modelleur
  // in de Explorer-paneel ziet.
  useEffect(() => {
    if (!sourceWpId) {
      setTree(null);
      setSelectedConceptIds(new Set());
      setSelectedDiagramIds(new Set());
      return;
    }
    const sourceWp = workpackages.find((wp) => wp.id === sourceWpId);
    if (!sourceWp?.mainBranchId) return;
    const branchId = sourceWp.mainBranchId;
    let cancelled = false;
    api
      .getExplorerTree(workspaceId, sourceWpId, branchId)
      .then((res) => {
        if (cancelled) return;
        setTree(res);
        setSelectedConceptIds(new Set());
        setSelectedDiagramIds(new Set());
      })
      .catch((err) => {
        if (!cancelled) setError(explain(err));
      });
    return () => {
      cancelled = true;
    };
  }, [workspaceId, sourceWpId, workpackages]);

  const toggleConcept = (id: string) => {
    setSelectedConceptIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const toggleDiagram = (id: string) => {
    setSelectedDiagramIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const toggleGroup = (kind: "concept" | "diagram", ids: string[]) => {
    const setter = kind === "concept" ? setSelectedConceptIds : setSelectedDiagramIds;
    setter((prev) => {
      const next = new Set(prev);
      const allSelected = ids.every((id) => next.has(id));
      if (allSelected) {
        for (const id of ids) next.delete(id);
      } else {
        for (const id of ids) next.add(id);
      }
      return next;
    });
  };
  const toggleLayer = (layerKey: string) => {
    setCollapsedLayers((prev) => {
      const next = new Set(prev);
      if (next.has(layerKey)) next.delete(layerKey);
      else next.add(layerKey);
      return next;
    });
  };
  const clearAll = () => {
    setSelectedConceptIds(new Set());
    setSelectedDiagramIds(new Set());
  };

  const run = async (dryRun: boolean) => {
    if (!sourceWpId || !targetWpId) return;
    if (sourceWpId === targetWpId) {
      setError(t("settings.workpackages.copyErrorSame"));
      return;
    }
    const conceptIds = Array.from(selectedConceptIds);
    const diagramIds = Array.from(selectedDiagramIds);
    if (conceptIds.length === 0 && diagramIds.length === 0) {
      setError(t("settings.workpackages.copyErrorEmpty"));
      return;
    }
    setBusy(true);
    setError(null);
    setInfo(null);
    try {
      const res = await api.copyAcrossWorkpackages(workspaceId, targetWpId, {
        sourceWorkpackageId: sourceWpId,
        conceptIds,
        diagramIds,
        mode,
        dryRun,
      });
      setResult({ ...res, wasDryRun: dryRun });
      const summary = t("settings.workpackages.copySummary", {
        cc: res.concepts.created,
        cr: res.concepts.reused,
        ct: res.concepts.created + res.concepts.reused,
        rc: res.relations.created,
        rr: res.relations.reused,
        rt: res.relations.created + res.relations.reused,
        dc: res.diagrams.created,
        dr: res.diagrams.reused,
        dt: res.diagrams.created + res.diagrams.reused,
      });
      setInfo(
        dryRun
          ? t("settings.workpackages.copyDoneDryRun", { summary })
          : t("settings.workpackages.copyDoneRun", { summary }),
      );
    } catch (err) {
      setError(explain(err));
    } finally {
      setBusy(false);
    }
  };

  if (selectableWps.length < 2) {
    return (
      <section className="rounded-lg border border-(--ld-border) bg-(--ld-surface) p-5">
        <h2 className="font-semibold text-base text-(--ld-text)">
          {t("settings.workpackages.copyTitle")}
        </h2>
        <p className="mt-1 text-(--ld-muted) text-sm">{t("settings.workpackages.copyNeedTwo")}</p>
      </section>
    );
  }

  return (
    <section className="rounded-lg border border-(--ld-border) bg-(--ld-surface) p-5">
      <h2 className="font-semibold text-base text-(--ld-text)">
        {t("settings.workpackages.copyTitle")}
      </h2>
      <p className="mt-1 text-(--ld-muted) text-sm">{t("settings.workpackages.copyDescription")}</p>

      <div className="mt-4 grid grid-cols-2 gap-3">
        <label className="flex flex-col gap-1 text-sm">
          <span className="text-(--ld-text)">{t("settings.workpackages.copySourceLabel")}</span>
          <select
            className="rounded border border-(--ld-control) bg-(--ld-surface) px-3 py-2 text-sm focus:border-(--ld-accent) focus:outline-none"
            value={sourceWpId ?? ""}
            onChange={(e) => setSourceWpId(e.target.value || null)}
            disabled={busy}
          >
            {selectableWps.map((wp) => (
              <option key={wp.id} value={wp.id}>
                {wp.name}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1 text-sm">
          <span className="text-(--ld-text)">{t("settings.workpackages.copyTargetLabel")}</span>
          <select
            className="rounded border border-(--ld-control) bg-(--ld-surface) px-3 py-2 text-sm focus:border-(--ld-accent) focus:outline-none"
            value={targetWpId ?? ""}
            onChange={(e) => setTargetWpId(e.target.value || null)}
            disabled={busy}
          >
            {selectableWps.map((wp) => (
              <option key={wp.id} value={wp.id}>
                {wp.name}
              </option>
            ))}
          </select>
        </label>
      </div>

      <fieldset className="mt-4">
        <legend className="text-(--ld-text) text-sm">
          {t("settings.workpackages.copyModeLabel")}
        </legend>
        <div className="mt-1 flex flex-col gap-1 text-sm">
          <label className="flex items-start gap-2">
            <input
              type="radio"
              name="copy-mode"
              value="match"
              checked={mode === "match"}
              onChange={() => setMode("match")}
              disabled={busy}
              className="mt-0.5"
            />
            <span>
              <strong>{t("settings.workpackages.copyModeMatch")}</strong>
              {t("settings.workpackages.copyModeMatchDesc")}
            </span>
          </label>
          <label className="flex items-start gap-2">
            <input
              type="radio"
              name="copy-mode"
              value="always-new"
              checked={mode === "always-new"}
              onChange={() => setMode("always-new")}
              disabled={busy}
              className="mt-0.5"
            />
            <span>
              <strong>{t("settings.workpackages.copyModeAlwaysNew")}</strong>
              {t("settings.workpackages.copyModeAlwaysNewDesc")}
            </span>
          </label>
        </div>
      </fieldset>

      <div className="mt-4 flex items-center justify-between">
        <h3 className="font-medium text-(--ld-text) text-sm">
          {t("settings.workpackages.copySourceContent")}
        </h3>
        <button
          type="button"
          onClick={clearAll}
          disabled={busy || (selectedConceptIds.size === 0 && selectedDiagramIds.size === 0)}
          className="rounded border border-(--ld-control) bg-(--ld-surface) px-2 py-0.5 text-(--ld-text) text-xs hover:bg-(--ld-raised) disabled:opacity-40"
        >
          {t("settings.workpackages.copyClearSelection")}
        </button>
      </div>

      <div className="mt-2 grid grid-cols-2 gap-3">
        <ExplorerSelector
          title={t("settings.workpackages.copyObjects")}
          tree={tree}
          kind="concept"
          collapsedLayers={collapsedLayers}
          toggleLayer={toggleLayer}
          selectedIds={selectedConceptIds}
          toggleItem={toggleConcept}
          toggleGroup={(ids) => toggleGroup("concept", ids)}
          busy={busy}
        />
        <ExplorerSelector
          title={t("settings.workpackages.copyDiagrams")}
          tree={tree}
          kind="diagram"
          collapsedLayers={collapsedLayers}
          toggleLayer={toggleLayer}
          selectedIds={selectedDiagramIds}
          toggleItem={toggleDiagram}
          toggleGroup={(ids) => toggleGroup("diagram", ids)}
          busy={busy}
        />
      </div>

      <p className="mt-1 text-(--ld-muted) text-xs">
        {t("settings.workpackages.copySelectionHint", {
          objects: selectedConceptIds.size,
          diagrams: selectedDiagramIds.size,
        })}
      </p>

      {error && (
        <p className="mt-3 rounded border border-(--ld-control) bg-(--ld-negative-bg) px-3 py-2 text-(--ld-negative-ink) text-sm">
          {error}
        </p>
      )}
      {info && (
        <p className="mt-3 rounded border border-transparent bg-(--ld-positive-bg) px-3 py-2 text-(--ld-positive-ink) text-sm">
          {info}
        </p>
      )}

      <div className="mt-4 flex gap-2">
        <button
          type="button"
          onClick={() => void run(true)}
          disabled={
            busy ||
            (selectedConceptIds.size === 0 && selectedDiagramIds.size === 0) ||
            sourceWpId === targetWpId
          }
          className="rounded border border-(--ld-control) bg-(--ld-surface) px-3 py-1.5 font-medium text-(--ld-text) text-sm hover:bg-(--ld-raised) disabled:opacity-50"
        >
          {t("settings.workpackages.copyDryRun")}
        </button>
        <button
          type="button"
          onClick={() => void run(false)}
          disabled={
            busy ||
            (selectedConceptIds.size === 0 && selectedDiagramIds.size === 0) ||
            sourceWpId === targetWpId
          }
          className="rounded bg-(--ld-primary) px-3 py-1.5 font-medium text-sm text-(--ld-primary-ink) hover:bg-(--ld-primary-hover) disabled:bg-(--ld-disabled-bg) disabled:text-(--ld-disabled-ink)"
        >
          {t("settings.workpackages.copyRun")}
        </button>
      </div>

      {result && (
        <div className="mt-5 rounded border border-(--ld-border) bg-(--ld-raised) p-3">
          <p className="font-medium text-(--ld-text) text-sm">
            {t("settings.workpackages.copyResult")}
            {result.wasDryRun ? t("settings.workpackages.copyResultDryRun") : ""}
          </p>
          <table className="mt-2 w-full text-xs">
            <thead>
              <tr className="text-left text-(--ld-muted)">
                <th className="py-0.5 pr-3 font-medium">
                  {t("settings.workpackages.copyColPass")}
                </th>
                <th className="py-0.5 pr-3 font-medium">
                  {t("settings.workpackages.copyColReused")}
                </th>
                <th className="py-0.5 pr-3 font-medium">
                  {t("settings.workpackages.copyColCreated")}
                </th>
                <th className="py-0.5 font-medium">{t("settings.workpackages.copyColSkipped")}</th>
              </tr>
            </thead>
            <tbody className="text-(--ld-text)">
              <tr>
                <td className="py-0.5 pr-3">{t("settings.workpackages.copyRowConcepts")}</td>
                <td className="py-0.5 pr-3">{result.concepts.reused}</td>
                <td className="py-0.5 pr-3">{result.concepts.created}</td>
                <td className="py-0.5">{result.concepts.skipped}</td>
              </tr>
              <tr>
                <td className="py-0.5 pr-3">{t("settings.workpackages.copyRowRelations")}</td>
                <td className="py-0.5 pr-3">{result.relations.reused}</td>
                <td className="py-0.5 pr-3">{result.relations.created}</td>
                <td className="py-0.5">{result.relations.skipped}</td>
              </tr>
              <tr>
                <td className="py-0.5 pr-3">{t("settings.workpackages.copyRowDiagrams")}</td>
                <td className="py-0.5 pr-3">{result.diagrams.reused}</td>
                <td className="py-0.5 pr-3">{result.diagrams.created}</td>
                <td className="py-0.5">{result.diagrams.skipped}</td>
              </tr>
            </tbody>
          </table>
          <details className="mt-2 text-xs">
            <summary className="cursor-pointer text-(--ld-text)">
              {t("settings.workpackages.copyDetails")}
            </summary>
            <ul className="mt-1 flex flex-col gap-0.5">
              {result.details.map((d) => {
                const actionColor =
                  d.action === "created"
                    ? "text-(--ld-positive-ink)"
                    : d.action === "reused"
                      ? "text-(--ld-accent)"
                      : "text-(--ld-muted)";
                const kindIcon = d.kind === "concept" ? "▪" : d.kind === "relation" ? "→" : "📐";
                const label =
                  resolveBilingual(d.name, i18n.language) ||
                  t("settings.workpackages.copyDetailUnnamed", { kind: d.kind });
                return (
                  <li key={`${d.kind}-${d.sourceId}`} className="flex items-center gap-2 text-xs">
                    <span className="text-(--ld-muted)">{kindIcon}</span>
                    <span className={`w-16 shrink-0 font-medium ${actionColor}`}>{d.action}</span>
                    <span className="truncate text-(--ld-text)">{label}</span>
                    <span className="shrink-0 text-(--ld-muted)">{d.typeId}</span>
                    {d.reason && (
                      <span className="ml-2 truncate text-(--ld-muted) italic">— {d.reason}</span>
                    )}
                  </li>
                );
              })}
            </ul>
          </details>
        </div>
      )}
    </section>
  );
}

function explain(err: unknown): string {
  if (err instanceof ApiError) {
    return err.body?.detail ?? err.body?.title ?? `HTTP ${err.status}`;
  }
  if (err instanceof Error) {
    return err.message;
  }
  return String(err);
}
